import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScanned: (token: string) => void;
}

const ELEMENT_ID = "bayzo-qr-reader";

const QrScanner = ({ open, onOpenChange, onScanned }: Props) => {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const start = async () => {
      try {
        const scanner = new Html5Qrcode(ELEMENT_ID);
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decoded) => {
            const match = decoded.match(/^bayzo:spot:(.+)$/);
            const token = match ? match[1] : decoded;
            if (cancelled) return;
            cancelled = true;
            scanner.stop().catch(() => {});
            onScanned(token);
            onOpenChange(false);
          },
          () => {}
        );
      } catch (e: any) {
        setError(e?.message || "Camera error");
        toast({ title: "Camera error", description: e?.message || "Unable to start camera", variant: "destructive" });
      }
    };
    start();
    return () => {
      cancelled = true;
      scannerRef.current?.stop().catch(() => {}).finally(() => {
        scannerRef.current?.clear();
        scannerRef.current = null;
      });
    };
  }, [open, onOpenChange, onScanned, toast]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Scan Spot QR</DialogTitle>
        </DialogHeader>
        <div id={ELEMENT_ID} className="w-full rounded-md overflow-hidden bg-black aspect-square" />
        {error && <p className="text-sm text-destructive mt-2">{error}</p>}
        <Button variant="outline" onClick={() => onOpenChange(false)} className="mt-2">Cancel</Button>
      </DialogContent>
    </Dialog>
  );
};

export default QrScanner;
